import { useCharactersStore } from '@/features/shared/stores/characters.store'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createTextNode, $getNodeByKey, ElementNode } from 'lexical'
import { forwardRef, useEffect, useRef } from 'react'
import { Position, SuggestionListItem } from '../type'
import { sceneSuggestions, transitionSuggestions } from './constants'
import { NodeType } from './nodes/NodeCore'
import { SceneNode } from './nodes/SceneNode'
import { useSpEditorProvider } from './SpEditorProvider'
import { SuggestionDropdown } from './SuggestionMenu'
import { getCurrentNode } from './utils/NodeUtils'

interface SuggestionBoxProps {
    position: Position
    editor: ReturnType<typeof useLexicalComposerContext>[0]
}

const ZERO_WIDTH_RE = /(?:\u200B|\u200C|\u200D|\uFEFF|\u2060|\u180E)/gu

const normalizeText = (text: string | undefined | null): string => {
    if (!text) return ''
    return text
        .replace(ZERO_WIDTH_RE, '')
        .normalize('NFKC')
        .trim()
        .replace(/\s+/g, ' ')
}

const getCharacterOptions = (characters: any[]): SuggestionListItem[] => {
    return characters
        .filter((c) => c?.name || c?.fullname)
        .map((c) => ({
            ...c,
            title: normalizeText(c.name ?? c.fullname),
        })) as SuggestionListItem[]
}

const getSceneOptions = (scenes: SceneNode[], editor: any): SuggestionListItem[] => {
    const existingScenes = scenes
        .map((s) =>
            editor.getEditorState().read(() => {
                const latestNode = $getNodeByKey(s.getKey())
                return latestNode ? getSceneHeader(latestNode as SceneNode) : null
            })
        )
        .filter(Boolean) as string[]

    const uniqueOptions = new Map<string, SuggestionListItem>()

    const addOption = (title: string) => {
        const normalized = normalizeText(title)
        if (normalized && normalized !== '[No Title]') {
            const key = normalized.toUpperCase()
            if (!uniqueOptions.has(key)) {
                uniqueOptions.set(key, { title: normalized })
            }
        }
    }

    sceneSuggestions.forEach((s) => addOption(s.title))
    existingScenes.forEach((title) => addOption(title))

    return Array.from(uniqueOptions.values())
}

const getTransitionOptions = (): SuggestionListItem[] => {
    return transitionSuggestions.map((s) => ({
        ...s,
        title: normalizeText(s.title),
    }))
}

const SpSuggestionBox = forwardRef<HTMLDivElement, SuggestionBoxProps>(({ editor, position }, ref) => {
    const { setSuggestionProps, suggestionProps } = useSpEditorProvider()
    const { characters } = useCharactersStore()
    const { scenes } = useSceneStore()

    // Keep latest data in refs so the effect always reads fresh values
    // without needing to re-run when data changes while the box is hidden.
    const charactersRef = useRef(characters)
    const scenesRef = useRef(scenes)
    charactersRef.current = characters
    scenesRef.current = scenes

    // coords transitions undefined → Position exactly when a new node is created
    // and the plugin decides to show the suggestion box. Use it as the trigger.
    const coords = suggestionProps.coords

    useEffect(() => {
        if (!coords) {
            // Box just closed — clear options
            setSuggestionProps((prev) => ({ ...prev, options: [] }))
            return
        }

        // Read node type fresh at the moment the box opens
        const nodeType = editor.getEditorState().read(() => {
            return getCurrentNode()?.getType() as NodeType
        })

        let options: SuggestionListItem[] = []
        switch (nodeType) {
            case NodeType.Character:
                options = getCharacterOptions(charactersRef.current)
                break
            case NodeType.Scene:
                options = getSceneOptions(scenesRef.current, editor)
                break
            case NodeType.Transition:
                options = getTransitionOptions()
                break
        }

        setSuggestionProps((prev) => ({ ...prev, options }))
    }, [coords, editor])

    return (
        <SuggestionDropdown
            ref={ref}
            onSelect={(c) => {
                editor.update(() => {
                    const node = getCurrentNode() as ElementNode
                    node?.append($createTextNode(c.title))
                    node?.selectEnd()
                })
            }}
        />
    )
})

export default SpSuggestionBox
